import { set, startOfDay } from "date-fns";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { z } from "zod";

import {
	ButtonStandard,
	DatePicker,
	FormField,
	type FormHandle,
	FormInputText,
	FormSingleSelect,
	FormSubscribe,
	ModalBase,
	PYForm,
	Text,
	toast,
} from "@performyard/py-components";
import type { ActionItemWithMeetingInfoJson } from "@performyard/service-contracts";

import { SingleSelectEmployee } from "../../../../components/Inputs/SelectEmployee";
import { StaticToast } from "../../../../components/Toast";

import { useEditMeetingActionItem, useMeetingWorkspaceDetails } from "../../../../api/hooks";

type EditActionItemModalProps = {
	isOpen: boolean;
	handleClose: () => void;
	actionItem: ActionItemWithMeetingInfoJson | null;
};

type EmployeeValue = {
	id: string;
	name: string;
};

const formSchema = z.object({
	name: z
		.string()
		.min(1, { message: "This is a required field" })
		.max(280, { message: "Name cannot exceed 280 characters" }),
	status: z.enum(["complete", "incomplete"]),
	assignee: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.nullable(),
	dueDate: z.date().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

const EditActionItemModal = ({ isOpen, handleClose, actionItem }: EditActionItemModalProps) => {
	const formRef = useRef<FormHandle<typeof formSchema>>(null);
	const dueDateDirtyRef = useRef(false);

	const { mutate: editActionItem, isPending } = useEditMeetingActionItem();
	const { data: workspace } = useMeetingWorkspaceDetails(actionItem?.workspaceId || "");

	const assignee = actionItem?.participants.find((p) => p.role === "assignee");

	const workspaceParticipants = useMemo(() => {
		if (!workspace?.participants) return [];
		return workspace.participants
			.filter((p) => !!p.name)
			.map((p) => ({
				id: p.userId,
				name: p.name || "",
			}));
	}, [workspace?.participants]);

	const handleFetchEmployees = useCallback(
		async (searchTerm: string) => {
			const lowerSearchTerm = searchTerm.toLowerCase();
			return workspaceParticipants.filter((participant) =>
				participant.name.toLowerCase().includes(lowerSearchTerm)
			);
		},
		[workspaceParticipants]
	);

	const getDefaultValues = (): FormValues => {
		if (!actionItem) {
			return {
				name: "",
				status: "incomplete",
				assignee: null,
				dueDate: null,
			};
		}

		const status: "complete" | "incomplete" =
			actionItem.mostRecentMeetingStatus === "deleted" ? "incomplete" : actionItem.mostRecentMeetingStatus;

		return {
			name: actionItem.name,
			status,
			assignee: assignee
				? {
						id: assignee.userId,
						name: assignee.name || "",
					}
				: null,
			// actionItem.dueDate arrives as a JSON string — coerce to a proper Date
			// so the form field, DatePicker, and dueDateChanged comparison all work with the same type
			dueDate: actionItem.dueDate ? new Date(actionItem.dueDate) : null,
		};
	};

	// Reset form when modal opens with new action item
	useEffect(() => {
		if (isOpen && actionItem && formRef.current) {
			formRef.current.reset();
			dueDateDirtyRef.current = false;
		}
	}, [isOpen, actionItem?._id, actionItem?.dueDate]);

	const onSubmit = ({ value }: { value: FormValues }) => {
		if (!actionItem) return;

		const participants = [...actionItem.participants];
		const assigneeIndex = participants.findIndex((p) => p.role === "assignee");

		if (value.assignee?.id) {
			// Update or add assignee
			if (assigneeIndex !== -1) {
				participants[assigneeIndex] = { userId: value.assignee.id, role: "assignee" };
			} else {
				participants.push({ userId: value.assignee.id, role: "assignee" });
			}
		} else if (assigneeIndex !== -1) {
			// Remove assignee if none selected
			participants.splice(assigneeIndex, 1);
		}

		// Update meetings with new status for most recent meeting
		const updatedMeetings = actionItem.meetings.map((m) =>
			m.meetingId === actionItem.mostRecentMeetingId ? { meetingId: m.meetingId, status: value.status } : m
		);

		// Only include dueDate in the payload when the user actually interacted with the date picker.
		// This allows editing name/status on overdue items without re-validating the existing past due date.
		const normalizedDueDate = value.dueDate
			? set(value.dueDate, { hours: 17, minutes: 0, seconds: 0, milliseconds: 0 }).toISOString()
			: null;

		editActionItem(
			{
				actionItemId: actionItem._id,
				data: {
					name: value.name,
					participants,
					meetings: updatedMeetings,
					...(dueDateDirtyRef.current && { dueDate: normalizedDueDate }),
				},
			},
			{
				onSuccess: () => {
					toast.success("Action Item Updated");
					onClose();
				},
				onError: () => {
					toast.error("Failed to update action item");
				},
			}
		);
	};

	const onClose = () => {
		if (formRef.current) {
			formRef.current.reset();
		}
		handleClose();
	};

	const statusOptions: Array<{ value: "incomplete" | "complete"; label: string }> = [
		{ value: "incomplete", label: "Incomplete" },
		{ value: "complete", label: "Complete" },
	];

	return (
		<ModalBase
			isOpen={isOpen && actionItem !== null}
			onClose={onClose}
			dangerouslySetClassName="min-h-[464px] min-w-[432px] overflow-visible"
		>
			<div className="flex flex-col px-4 pt-4 pb-6">
				<div className="flex items-center justify-center border-b border-neutral-2 pb-4">
					<Text weight="semi-bold" size="sub-heading">
						Edit Action Item
					</Text>
				</div>
				<PYForm
					ref={formRef}
					id="edit-action-item-form"
					schema={formSchema}
					defaultValues={getDefaultValues()}
					onSubmit={onSubmit}
					validateOn={["onSubmit"]}
				>
					<div className="space-y-4">
						<FormInputText
							id="name"
							label="Action Item Name"
							placeholder="Action Item Name"
							clearable
							data-testid="action-item-name-input"
						/>

						<FormSingleSelect<"incomplete" | "complete">
							id="status"
							label="Status"
							options={statusOptions}
							selectionVariant="select"
							placeholder="Select status"
						/>

						<FormField<EmployeeValue | null>
							id="assignee"
							label="Assignee"
							input={(field) => (
								<SingleSelectEmployee
									value={field.value ?? undefined}
									onChange={(employeeId, employeeName) => {
										if (employeeId && employeeName) {
											field.onChange({ id: employeeId, name: employeeName });
										} else {
											field.onChange(null);
										}
									}}
									fetchEmployees={handleFetchEmployees}
									placeholder="Make a selection"
									isClearable
								/>
							)}
						/>

						<FormField<Date | null>
							id="dueDate"
							label="Due Date"
							input={(field) => (
								<DatePicker
									allowEmpty
									value={field.value}
									onChange={(date) => {
										dueDateDirtyRef.current = true;
										field.onChange(date);
									}}
									startDate={startOfDay(new Date())}
									clearable
									showMonthAndYearDropdowns
									popoverClassName="z-[9999]"
								/>
							)}
						/>

						<FormSubscribe id="due-date-info" selector={(state) => state.values.dueDate}>
							{(dueDate) =>
								dueDate ? (
									<div className="flex items-center justify-center">
										<StaticToast
											message="Due Date is set for 5:00pm in your current time zone"
											type="info"
											size="small"
										/>
									</div>
								) : null
							}
						</FormSubscribe>
					</div>
					<div className="flex justify-center gap-2 border-t border-neutral-2 pt-6">
						<div className="w-fit">
							<ButtonStandard size="large" onClick={onClose} disabled={isPending}>
								Cancel
							</ButtonStandard>
						</div>
						<div className="w-fit">
							<ButtonStandard size="large" color="green" type="submit" disabled={isPending}>
								Save
							</ButtonStandard>
						</div>
					</div>
				</PYForm>
			</div>
		</ModalBase>
	);
};

export default EditActionItemModal;
